// src/ProfilPage.js
import * as React from "react";
import { useState, useEffect } from "react";
import { useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const ProfilPage = () => {
    const notify = useNotify();

    const [loading, setLoading]       = useState(true);
    const [saving, setSaving]         = useState(false);
    const [nume, setNume]             = useState('');
    const [prenume, setPrenume]       = useState('');
    const [telefon, setTelefon]       = useState('');
    const [email, setEmail]           = useState('');
    const [parola, setParola]         = useState('');
    const [parolaConf, setParolaConf] = useState('');

    const colaboratorId = localStorage.getItem('colaborator_id');

    useEffect(() => {
        if (!colaboratorId) return;
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/colaboratori/${colaboratorId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.json())
            .then((data) => {
                setNume(data.nume || '');
                setPrenume(data.prenume || '');
                setTelefon(data.telefon || '');
                setEmail(data.email || '');
            })
            .catch(() => notify('Eroare la încărcarea profilului', { type: 'error' }))
            .finally(() => setLoading(false));
    }, [colaboratorId]);

    const handleSave = async () => {
        if (parola && parola !== parolaConf) {
            notify('Parolele nu coincid', { type: 'error' });
            return;
        }

        setSaving(true);
        const token = localStorage.getItem('token');
        const payload = { telefon, email };
        if (parola) payload.password = parola;

        try {
            const r = await fetch(`${API_URL}/colaboratori/${colaboratorId}/profil`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/merge-patch+json',
                },
                body: JSON.stringify(payload),
            });

            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err['hydra:description'] || 'Eroare la salvare');
            }

            notify('Profil actualizat cu succes', { type: 'success' });
            setParola('');
            setParolaConf('');
        } catch (e) {
            notify(e.message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 520, mx: 'auto', mt: 4, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AccountCircleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight={700}>
                    Profilul meu
                </Typography>
            </Box>

            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    {/* Date de identitate - readonly */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Date personale
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField fullWidth label="Nume"    value={nume}    disabled size="small" />
                        <TextField fullWidth label="Prenume" value={prenume} disabled size="small" />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Date editabile */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Date de contact
                    </Typography>
                    <TextField
                        fullWidth label="Telefon" value={telefon}
                        onChange={(e) => setTelefon(e.target.value)}
                        size="small" sx={{ mb: 2 }} inputProps={{ maxLength: 20 }}
                    />
                    <TextField
                        fullWidth label="Email" type="email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small" sx={{ mb: 2 }}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Schimbare parola */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Schimbare parolă{' '}
                        <Typography component="span" variant="caption">(opțional)</Typography>
                    </Typography>
                    <TextField
                        fullWidth label="Parolă nouă" type="password"
                        value={parola} onChange={(e) => setParola(e.target.value)}
                        size="small" sx={{ mb: 2 }} autoComplete="new-password"
                    />
                    <TextField
                        fullWidth label="Confirmă parola nouă" type="password"
                        value={parolaConf} onChange={(e) => setParolaConf(e.target.value)}
                        size="small" sx={{ mb: 3 }} autoComplete="new-password"
                        error={!!parolaConf && parola !== parolaConf}
                        helperText={!!parolaConf && parola !== parolaConf ? 'Parolele nu coincid' : ''}
                    />

                    <Button
                        fullWidth variant="contained" size="large"
                        onClick={handleSave} disabled={saving} sx={{ py: 1.5 }}
                    >
                        {saving ? <CircularProgress size={24} color="inherit" /> : 'Salvează'}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};
